import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2, MapPinned } from 'lucide-react';
import { toast } from 'sonner';
import { AddressEnrichmentPanel } from '@/components/tickets/AddressEnrichmentPanel';
import { fetchDmpAddress, attachJvid, detachJvid } from '@/hooks/useTickets';
import type { TicketDetail, DmpAddressResponse } from '@uniticket/shared';

/**
 * Extra addresses a ticket refers to, beyond its own JVID.
 *
 * Built for Build tickets, where the ticket is keyed on an area and a question often concerns
 * several addresses. Each row opens the standard enrichment drawer for that JVID — the same
 * fields, role curation and order drill-down as any JVID-based ticket — through
 * AddressEnrichmentPanel's `jvid` prop rather than a second implementation.
 *
 * Addresses are resolved lazily, one request per JVID, so opening a ticket never fans out into N
 * DMP lookups before anyone asks.
 */
export function AttachedJvidsPanel({
  ticket,
  onChange,
}: {
  ticket: TicketDetail;
  onChange?: (jvids: number[]) => void;
}) {
  const { t } = useTranslation(['tickets', 'common']);
  const [jvids, setJvids] = useState<number[]>(ticket.attachedJvids ?? []);
  const [addresses, setAddresses] = useState<Record<number, DmpAddressResponse | 'error'>>({});
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  /** JVIDs whose address lookup has been started, so re-renders never refetch. */
  const resolvedRef = useRef<Set<number>>(new Set());

  useEffect(() => { setJvids(ticket.attachedJvids ?? []); }, [ticket.attachedJvids]);

  useEffect(() => {
    for (const jvid of jvids) {
      if (resolvedRef.current.has(jvid)) continue;
      resolvedRef.current.add(jvid);
      fetchDmpAddress(String(jvid))
        .then((res) => setAddresses((a) => ({ ...a, [jvid]: res.data })))
        .catch(() => setAddresses((a) => ({ ...a, [jvid]: 'error' })));
    }
  }, [jvids]);

  async function handleAdd() {
    const jvid = parseInt(input.trim(), 10);
    if (isNaN(jvid) || input.trim().length !== 8) {
      toast.error(t('tickets:attachedJvids.invalid'));
      return;
    }
    if (jvids.includes(jvid)) {
      toast.info(t('tickets:attachedJvids.alreadyAdded'));
      setInput('');
      return;
    }
    setBusy(true);
    try {
      const res = await attachJvid(ticket.id, jvid);
      setJvids(res.data);
      onChange?.(res.data);
      setInput('');
    } catch (err: any) {
      toast.error(err?.message || t('tickets:attachedJvids.addError'));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(jvid: number) {
    setBusy(true);
    try {
      const res = await detachJvid(ticket.id, jvid);
      setJvids(res.data);
      onChange?.(res.data);
    } catch (err: any) {
      toast.error(err?.message || t('tickets:attachedJvids.removeError'));
    } finally {
      setBusy(false);
    }
  }

  function label(jvid: number): string {
    const a = addresses[jvid];
    if (a === undefined) return t('tickets:attachedJvids.resolving');
    if (a === 'error') return t('tickets:attachedJvids.unknownAddress');
    return [a.streetName, a.houseNumber, a.busNumber && `bus ${a.busNumber}`].filter(Boolean).join(' ')
      + (a.postalCode || a.city ? ` — ${[a.postalCode, a.city].filter(Boolean).join(' ')}` : '');
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-3">
        <MapPinned className="w-4 h-4 text-slate-400" />
        <h3 className="text-sm font-bold text-slate-800">{t('tickets:attachedJvids.title')}</h3>
        {jvids.length > 0 && (
          <span className="text-xs font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">{jvids.length}</span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">{t('tickets:attachedJvids.hint')}</p>

      {jvids.length === 0 ? (
        <p className="text-sm text-slate-400 mb-4">{t('tickets:attachedJvids.none')}</p>
      ) : (
        <ul className="space-y-2 mb-4">
          {jvids.map((jvid) => (
            <li key={jvid} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
              <span className="font-mono text-xs font-semibold text-brand-rose shrink-0">{jvid}</span>
              <span className="text-xs text-slate-600 truncate flex-1">{label(jvid)}</span>
              <AddressEnrichmentPanel
                ticket={ticket}
                jvid={jvid}
                title={`${t('tickets:enrichment.title')} — ${jvid}`}
                triggerLabel={t('tickets:attachedJvids.viewData')}
              />
              <button
                type="button"
                onClick={() => handleRemove(jvid)}
                disabled={busy}
                title={t('tickets:attachedJvids.remove')}
                className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2 max-w-sm">
        <input
          type="text"
          inputMode="numeric"
          value={input}
          onChange={(e) => setInput(e.target.value.replace(/\D/g, '').slice(0, 8))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAdd(); } }}
          placeholder={t('tickets:attachedJvids.placeholder')}
          className="flex-1 px-3 py-2 text-sm font-mono border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
        />
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={busy || input.trim().length !== 8}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {t('tickets:attachedJvids.add')}
        </button>
      </div>
    </div>
  );
}
